import { useEffect, useRef, type MouseEvent, type PointerEvent } from 'react';

import { dashboardT } from '../../../../../../lib/messages';
import {
  madBuyLineItemWeekDays,
  type MadBuyLineItemDayTimeActivity,
} from '../../../lineItemsModel';
import {
  formatScheduleHourLabel,
  isEveryDayHourSelected,
  scheduleGuideLabels,
  scheduleHours,
  setEveryDayHour,
  setScheduleHour,
  type ScheduleDragMode,
} from '../LineItemSchedule.helpers';
import { LineItemScheduleDayRow } from '../LineItemScheduleDayRow';
import { ScheduleHourButton } from '../ScheduleHourButton';

export function LineItemScheduleGrid({
  activities,
  onActivitiesChange,
  timezoneLabel,
}: {
  activities: MadBuyLineItemDayTimeActivity[];
  onActivitiesChange: (activities: MadBuyLineItemDayTimeActivity[]) => void;
  timezoneLabel: string;
}) {
  const dragModeRef = useRef<ScheduleDragMode | null>(null);
  const activitiesRef = useRef(activities);
  const suppressNextClickRef = useRef(false);
  const suppressClickResetTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    activitiesRef.current = activities;
  }, [activities]);

  useEffect(() => {
    function stopDragSelection() {
      dragModeRef.current = null;
      if (suppressClickResetTimeoutRef.current !== null) {
        window.clearTimeout(suppressClickResetTimeoutRef.current);
      }

      suppressClickResetTimeoutRef.current = window.setTimeout(() => {
        suppressNextClickRef.current = false;
        suppressClickResetTimeoutRef.current = null;
      }, 0);
    }

    window.addEventListener('pointerup', stopDragSelection);
    window.addEventListener('pointercancel', stopDragSelection);

    return () => {
      window.removeEventListener('pointerup', stopDragSelection);
      window.removeEventListener('pointercancel', stopDragSelection);
      if (suppressClickResetTimeoutRef.current !== null) {
        window.clearTimeout(suppressClickResetTimeoutRef.current);
      }
    };
  }, []);

  function applyDayHour(
    day: Parameters<typeof setScheduleHour>[1],
    hour: number,
    mode: ScheduleDragMode,
  ) {
    const nextActivities = setScheduleHour(activitiesRef.current, day, hour, mode);
    activitiesRef.current = nextActivities;
    onActivitiesChange(nextActivities);
  }

  function applyEveryDayHour(hour: number, mode: ScheduleDragMode) {
    const nextActivities = setEveryDayHour(activitiesRef.current, hour, mode);
    activitiesRef.current = nextActivities;
    onActivitiesChange(nextActivities);
  }

  function startDragSelection(selected: boolean, applySelection: (mode: ScheduleDragMode) => void) {
    const nextMode: ScheduleDragMode = selected ? 'remove' : 'add';
    dragModeRef.current = nextMode;
    suppressNextClickRef.current = true;
    applySelection(nextMode);
  }

  function continueDragSelection(applySelection: (mode: ScheduleDragMode) => void) {
    if (!dragModeRef.current) {
      return;
    }

    applySelection(dragModeRef.current);
  }

  function handleClickToggle(
    event: MouseEvent<HTMLButtonElement>,
    applySelection: (mode: ScheduleDragMode) => void,
  ) {
    if (suppressNextClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    applySelection('toggle');
  }

  function handlePointerDragStart(
    event: PointerEvent<HTMLButtonElement>,
    selected: boolean,
    applySelection: (mode: ScheduleDragMode) => void,
  ) {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    startDragSelection(selected, applySelection);
  }

  return (
    <section className="border-border bg-card overflow-hidden rounded-md border">
      <div className="border-border border-b px-4 py-3">
        <h3 className="text-foreground text-sm font-semibold">
          {dashboardT('madBuy.lineItems.dayAndHourDelivery')}
        </h3>
        <p className="text-muted-foreground mt-1 text-xs">
          {dashboardT('madBuy.lineItems.dayAndHourDeliveryDescription', {
            timezone: timezoneLabel,
          })}
        </p>
      </div>
      <div className="overflow-x-auto p-4">
        <div className="grid min-w-[920px] grid-cols-[96px_repeat(24,minmax(32px,1fr))] gap-1">
          <span aria-hidden="true" />
          {scheduleHours.map((hour) => (
            <span
              key={hour}
              className="text-muted-foreground h-5 text-center text-[10px] leading-5"
            >
              {scheduleGuideLabels[hour] ?? ''}
            </span>
          ))}

          <span className="text-muted-foreground flex h-8 items-center text-xs font-semibold">
            {dashboardT('madBuy.lineItems.everyDay')}
          </span>
          {scheduleHours.map((hour) => {
            const selected = isEveryDayHourSelected(activities, hour);

            return (
              <ScheduleHourButton
                key={`every-${String(hour)}`}
                ariaLabel={dashboardT('madBuy.lineItems.everyDayHour', {
                  hour: formatScheduleHourLabel(hour),
                })}
                selected={selected}
                onClick={(event) => {
                  handleClickToggle(event, (mode) => {
                    applyEveryDayHour(hour, mode);
                  });
                }}
                onPointerDown={(event) => {
                  handlePointerDragStart(event, selected, (mode) => {
                    applyEveryDayHour(hour, mode);
                  });
                }}
                onPointerEnter={() => {
                  continueDragSelection((mode) => {
                    applyEveryDayHour(hour, mode);
                  });
                }}
              />
            );
          })}

          {madBuyLineItemWeekDays.map((day) => (
            <LineItemScheduleDayRow
              key={day}
              activities={activities}
              day={day}
              onClick={(hour, event) => {
                handleClickToggle(event, (mode) => {
                  applyDayHour(day, hour, mode);
                });
              }}
              onPointerDown={(hour, selected, event) => {
                handlePointerDragStart(event, selected, (mode) => {
                  applyDayHour(day, hour, mode);
                });
              }}
              onPointerEnter={(hour) => {
                continueDragSelection((mode) => {
                  applyDayHour(day, hour, mode);
                });
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
